import type { AntennaModelRecord, EquipmentLifecycle, RadioDeviceRecord } from '@freedom/shared';

export type EquipmentSelectionContext = 'catalog' | 'ptp' | 'ap' | 'cpe';
export type EquipmentFrequencyMatch = 'compatible' | 'unknown' | 'incompatible';
export type EquipmentCatalogItem = RadioDeviceRecord | AntennaModelRecord;

export interface EquipmentCatalogGroup<T extends EquipmentCatalogItem> {
  lifecycle: EquipmentLifecycle;
  family: string;
  items: T[];
}

const lifecycleOrder: EquipmentLifecycle[] = ['current', 'vintage', 'legacy', 'discontinued', 'unknown'];
const familyOrder = ['Wave', 'LTU', 'airMAX AC', 'airMAX M', 'airFiber', 'Пассивные антенны', 'Пользовательские модели', 'Другие'];

function normalizedSearchToken(value: string): string {
  return value.toLocaleLowerCase('en-US').replace(/[^a-z0-9а-я]+/gu, '');
}

export function equipmentFamily(item: EquipmentCatalogItem): string {
  if (item.isCustom) return 'Пользовательские модели';
  const value = `${item.model} ${item.sku ?? ''} ${item.compatibilityTags.join(' ')}`.toLocaleLowerCase('en-US');
  if (value.includes('wave')) return 'Wave';
  if (value.includes('ltu')) return 'LTU';
  if (value.includes('airfiber') || /\baf-?\d/u.test(value)) return 'airFiber';
  if (value.includes('airmax-ac') || /(?:\bac\b|5ac|r5ac|pbe-5ac|lbe-5ac|nbe-5ac)/u.test(value)) return 'airMAX AC';
  if (value.includes('airmax-m') || /(?:nanostation|locom|rocketm|bullet m|nanobeam m|powerbeam m|powerbridge|airgrid|picostation)/u.test(value)) return 'airMAX M';
  if (!('deviceType' in item)) return 'Пассивные антенны';
  return 'Другие';
}

export function equipmentSearchAliases(item: EquipmentCatalogItem): string[] {
  const aliases = new Set<string>([
    item.model,
    item.sku ?? '',
    equipmentFamily(item),
    ...item.compatibilityTags,
    ...item.supportedBands,
  ]);
  const sku = item.sku?.toLocaleUpperCase('en-US') ?? '';
  if (sku === 'ROCKETM5') aliases.add('RM5');
  if (sku === 'ROCKETM2') aliases.add('RM2');
  if (sku.startsWith('LOCOM5')) aliases.add('LOCO M5');
  if (sku.startsWith('LOCOM2')) aliases.add('LOCO M2');
  if (sku.startsWith('R5AC')) aliases.add('R5AC');
  if (sku.startsWith('PBE-5AC')) aliases.add('PBE 5AC');
  return [...aliases].filter(Boolean);
}

export function equipmentMatchesSearch(item: EquipmentCatalogItem, query: string): boolean {
  const needle = normalizedSearchToken(query.trim());
  if (!needle) return true;
  return equipmentSearchAliases(item).some((candidate) => normalizedSearchToken(candidate).includes(needle));
}

export function equipmentMatchesContext(item: RadioDeviceRecord, context: EquipmentSelectionContext): boolean {
  if (context === 'catalog') return true;
  const requiredRole = context === 'ptp' ? 'ptp' : context === 'ap' ? 'ptmp-ap' : 'station';
  return item.roles.includes(requiredRole);
}

export function equipmentFrequencyMatch(item: EquipmentCatalogItem, frequencyMHz: number | null): EquipmentFrequencyMatch {
  if (frequencyMHz === null || !Number.isFinite(frequencyMHz)) return 'unknown';
  if (item.minFrequencyMHz !== null && item.maxFrequencyMHz !== null) {
    return frequencyMHz >= item.minFrequencyMHz && frequencyMHz <= item.maxFrequencyMHz ? 'compatible' : 'incompatible';
  }
  const knownBands = item.supportedBands.map((label) => {
    const normalized = label.toLocaleLowerCase('en-US');
    if (normalized.includes('900 mhz')) return frequencyMHz >= 850 && frequencyMHz <= 1_000;
    if (normalized.includes('2.4 ghz')) return frequencyMHz >= 2_300 && frequencyMHz <= 2_500;
    if (normalized.includes('3 ghz')) return frequencyMHz >= 3_000 && frequencyMHz <= 4_000;
    if (normalized.includes('5 ghz')) return frequencyMHz >= 4_800 && frequencyMHz <= 5_900;
    if (normalized.includes('6 ghz')) return frequencyMHz >= 5_925 && frequencyMHz <= 7_125;
    if (normalized.includes('10 ghz')) return frequencyMHz >= 10_000 && frequencyMHz <= 10_700;
    if (normalized.includes('11 ghz')) return frequencyMHz >= 10_700 && frequencyMHz <= 12_000;
    if (normalized.includes('24 ghz')) return frequencyMHz >= 24_000 && frequencyMHz <= 24_250;
    if (normalized.includes('60 ghz')) return frequencyMHz >= 57_000 && frequencyMHz <= 71_000;
    return null;
  }).filter((matches): matches is boolean => matches !== null);
  if (knownBands.length === 0) return 'unknown';
  return knownBands.some(Boolean) ? 'compatible' : 'incompatible';
}

export function groupEquipmentCatalog<T extends EquipmentCatalogItem>(items: T[]): EquipmentCatalogGroup<T>[] {
  const groups = new Map<string, EquipmentCatalogGroup<T>>();
  for (const item of items) {
    const family = equipmentFamily(item);
    const key = `${item.lifecycle}:${family}`;
    const group = groups.get(key) ?? { lifecycle: item.lifecycle, family, items: [] };
    group.items.push(item);
    groups.set(key, group);
  }
  for (const group of groups.values()) {
    group.items.sort((left, right) => `${left.model} ${left.sku ?? ''}`.localeCompare(`${right.model} ${right.sku ?? ''}`, 'en'));
  }
  return [...groups.values()].sort((left, right) => {
    const lifecycleDelta = lifecycleOrder.indexOf(left.lifecycle) - lifecycleOrder.indexOf(right.lifecycle);
    if (lifecycleDelta !== 0) return lifecycleDelta;
    const leftFamily = familyOrder.indexOf(left.family);
    const rightFamily = familyOrder.indexOf(right.family);
    return (leftFamily < 0 ? familyOrder.length : leftFamily) - (rightFamily < 0 ? familyOrder.length : rightFamily)
      || left.family.localeCompare(right.family, 'ru');
  });
}

export function equipmentContextLabel(context: EquipmentSelectionContext): string {
  if (context === 'ptp') return 'PtP';
  if (context === 'ap') return 'Base Station / AP';
  if (context === 'cpe') return 'Client / CPE';
  return 'Каталог';
}
